import { getOssSignedUrlExpiresAt } from './ossSignedUrl.js'

export const CONTENT_RESOURCE_REFRESH_SKEW_MS = 30_000

const firstUrl = (...values) => {
  for (const value of values) {
    const normalized = typeof value === 'string' ? value.trim() : ''
    if (normalized) return normalized
  }
  return ''
}

/**
 * @description 返回签名内容资源应提前刷新的等待时间；非签名资源无需刷新。
 * @param {string} value - 内容资源访问地址
 * @param {number} [nowMs=Date.now()] - 当前时间（供测试注入）
 * @param {number} [skewMs=CONTENT_RESOURCE_REFRESH_SKEW_MS] - 到期前预留时间
 * @returns {number|null} 等待毫秒数；null 表示无需定时刷新
 */
export function getContentResourceRefreshDelay(
  value,
  nowMs = Date.now(),
  skewMs = CONTENT_RESOURCE_REFRESH_SKEW_MS
) {
  const expiresAt = getOssSignedUrlExpiresAt(value)
  if (expiresAt === null) return null

  return Math.max(0, expiresAt - nowMs - Math.max(0, Number(skewMs) || 0))
}

export function createContentResourceRefreshGate() {
  let lastRequestedUrl = ''

  return {
    reset() {
      lastRequestedUrl = ''
    },
    shouldRequest(url) {
      const normalizedUrl = firstUrl(url)
      if (!normalizedUrl || normalizedUrl === lastRequestedUrl) return false
      lastRequestedUrl = normalizedUrl
      return true
    },
  }
}

/**
 * @description 在详情页实例内限制资源预览失败刷新，同时允许新签名地址续期。
 * @returns {{shouldRequest: (request: {contentId: string|number, reason: string, url: string}) => boolean}}
 */
export function createContentDetailRefreshGate() {
  let currentContentId = ''
  const failedResources = new Set()
  const expiringAccessUrls = new Set()

  return {
    shouldRequest({ contentId, reason, url } = {}) {
      const nextContentId = String(contentId || '').trim()
      const accessUrl = String(url || '').trim()
      if (!nextContentId || !accessUrl) return false

      if (nextContentId !== currentContentId) {
        currentContentId = nextContentId
        failedResources.clear()
        expiringAccessUrls.clear()
      }

      const isSignedExpiry = reason === 'signed-expiring'
      const key = isSignedExpiry ? accessUrl : accessUrl.replace(/[?#].*$/, '')
      const requested = isSignedExpiry ? expiringAccessUrls : failedResources
      if (requested.has(key)) return false
      requested.add(key)
      return true
    },
  }
}

/**
 * @description Normalize a content resource without replacing its stable URL.
 * previewUrl is an ephemeral bearer URL and is used only for the current view.
 */
export function normalizeContentResource(source = {}, fallback = {}) {
  const record = source && typeof source === 'object' ? source : {}
  const fallbackRecord = fallback && typeof fallback === 'object' ? fallback : {}
  const stableUrl = firstUrl(record.url, fallbackRecord.url)
  const accessUrl = firstUrl(
    record.fileUrl,
    record.previewUrl,
    record.url,
    record.downloadUrl,
    fallbackRecord.fileUrl,
    fallbackRecord.previewUrl,
    fallbackRecord.url,
    fallbackRecord.downloadUrl
  )

  return {
    ...record,
    downloadUrl: accessUrl,
    fileUrl: accessUrl,
    previewUrl: firstUrl(record.previewUrl, fallbackRecord.previewUrl, accessUrl),
    url: stableUrl || accessUrl,
  }
}

export function resolveContentCoverUrl(source = {}, fallback = {}) {
  const record = source && typeof source === 'object' ? source : {}
  const fallbackRecord = fallback && typeof fallback === 'object' ? fallback : {}
  return firstUrl(
    fallbackRecord.coverUrl,
    record.previewUrl,
    record.url,
    fallbackRecord.videoCover?.previewUrl,
    fallbackRecord.videoCover?.url
  )
}
